/**
 * Feature Flag context — provides useFeatureFlag() and useFeatureFlags() hooks.
 * Fetches flags from GET /api/v1/feature-flags on mount, caches in state,
 * and auto-refreshes every 5 minutes.
 *
 * Priority (highest wins):
 *   1. VITE_FF_* env vars (baked at build time via lib/featureFlags.ts)
 *   2. API response from GET /api/v1/feature-flags
 *   3. Coded defaults in FLAG_DEFAULTS
 *
 * Defaults apply immediately; falls back to cached/default values on API error.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { apiClient } from '@/services/api-client';
import {
  type FeatureFlagName,
  FLAG_DEFAULTS,
  INITIAL_FLAGS,
} from '@/lib/featureFlags';

// Re-export the type so callers can import from either path
export type { FeatureFlagName };

// ---------- Context type ----------

interface FeatureFlagContextValue {
  /** All flags as a record. Populated immediately from defaults; updated after API fetch. */
  flags: Record<string, boolean>;
  /** Whether the initial API fetch is still in progress. */
  isLoading: boolean;
  /** Re-fetch flags from the API immediately. */
  refresh: () => void;
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ---------- Context ----------

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

// ---------- Provider ----------

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  // Start with INITIAL_FLAGS (coded defaults + VITE_FF_* overrides) so the UI
  // renders with the correct values even before the API responds.
  const [flags, setFlags] = useState<Record<string, boolean>>(INITIAL_FLAGS);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchFlags = useCallback(async (signal?: AbortSignal) => {
    try {
      // API returns { "flags": { ... } } envelope
      // suppressToast:true + retries:0 — feature flag fetch is best-effort;
      // failures must never show user-facing error toasts or compete with
      // application API calls via retry backoff (caused Step 1 regression).
      const response = await apiClient.get<{ flags: Record<string, boolean> }>('/api/v1/feature-flags', { signal, suppressToast: true, retries: 0 });
      // Support both envelope format { flags: {...} } and legacy flat { FlagName: bool }
      const data: Record<string, boolean> =
        response && typeof response === 'object' && 'flags' in response
          ? (response as { flags: Record<string, boolean> }).flags
          : (response as unknown as Record<string, boolean>);
      // Merge: coded defaults < API response < VITE_FF_* env overrides (highest)
      // This ensures env overrides always win, even over API values.
      setFlags(() => ({ ...FLAG_DEFAULTS, ...data, ...INITIAL_FLAGS }));
    } catch (err: unknown) {
      // On error, keep last-known flags (defaults + env overrides). Don't clear state.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // A logged-out visitor gets 401 here before sign-in — expected, and the
      // cached/default flags already cover it. Only surface unexpected failures
      // (console-noise cleanup 2026-07-29).
      const flagStatus = (err as { status?: number } | null)?.status;
      if (flagStatus === 401 || flagStatus === 403) {
        if (import.meta.env.DEV) {
          console.debug('[FeatureFlags] not signed in — using cached/default values');
        }
      } else {
        console.warn('[FeatureFlags] Failed to fetch feature flags, using cached/default values', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch + periodic refresh
  useEffect(() => {
    const controller = new AbortController();
    fetchFlags(controller.signal);

    intervalRef.current = setInterval(() => {
      fetchFlags();
    }, REFRESH_INTERVAL_MS);

    return () => {
      controller.abort();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchFlags]);



  const refresh = useCallback(() => {
    fetchFlags();
  }, [fetchFlags]);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({ flags, isLoading, refresh }),
    [flags, isLoading, refresh],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {/* NavigationRefresher is only mounted when inside a Router context (e.g. in the app).
          It re-fetches flags on route change. In test environments without a Router,
          useInRouterContext() returns false and this sub-component is skipped. */}
      <NavigationRefresher onNavigate={refresh} />
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Internal component that re-fetches flags on each route change.
 * Rendered only when a Router context is present to avoid breaking tests
 * that render FeatureFlagProvider outside a Router.
 */
function NavigationRefresher({ onNavigate }: { onNavigate: () => void }) {
  const inRouter = useInRouterContext();
  if (!inRouter) return null;
  return <RouterLocationWatcher onNavigate={onNavigate} />;
}

/** Inner component — only rendered inside a Router, so useLocation() is safe here. */
function RouterLocationWatcher({ onNavigate }: { onNavigate: () => void }) {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip initial mount — the parent provider already fetches on mount.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onNavigate();
  // onNavigate is stable (wrapped in useCallback), so this only fires on pathname changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}

// ---------- Hooks ----------

/**
 * Returns the boolean value for a single feature flag.
 * Defaults to the coded default while loading or if the flag doesn't exist.
 */
export function useFeatureFlag(flag: FeatureFlagName): boolean {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    // Outside provider — fail-safe: use INITIAL_FLAGS (includes env overrides)
    return INITIAL_FLAGS[flag] ?? FLAG_DEFAULTS[flag] ?? false;
  }
  return ctx.flags[flag] ?? FLAG_DEFAULTS[flag] ?? false;
}

/**
 * True while the first `GET /api/v1/feature-flags` is still in flight.
 *
 * Until it lands, `flags` is INITIAL_FLAGS — the coded defaults — so a flag the
 * server turns ON still reads as its default. A component that merely *renders*
 * on a flag can ignore this and correct itself when the fetch resolves; one that
 * takes an irreversible action on it (a redirect) must wait, or it acts on the
 * default and never sees the answer. Outside the provider there is no fetch, so
 * INITIAL_FLAGS is already final and this is false.
 */
export function useFeatureFlagsLoading(): boolean {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) return false;
  return ctx.isLoading;
}

/**
 * Returns all feature flags as a `Record<string, boolean>`.
 */
export function useFeatureFlags(): Record<string, boolean> {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) return { ...INITIAL_FLAGS };
  return ctx.flags;
}

// ---------- FeatureGate Component ----------

/**
 * Conditionally renders children based on a feature flag.
 *
 * Usage:
 *   <FeatureGate flag="AiChat">
 *     <SophiaChat />
 *   </FeatureGate>
 *
 *   // With fallback when flag is off:
 *   <FeatureGate flag="I18n" fallback={<EnglishOnly />}>
 *     <LanguageSwitcher />
 *   </FeatureGate>
 */
export function FeatureGate({
  flag,
  children,
  fallback = null,
}: {
  flag: FeatureFlagName;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
}
